// Phase 29.6.3 fix · /admin/exception-rules — SUPER_ADMIN/MGMT settings.

import { useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const FIELDS = [
  ["late_attendance_cutoff_time", "Late attendance cutoff (IST, HH:MM)", "text"],
  ["beat_deviation_max_positions_off", "Beat deviation max positions off", "number"],
  ["low_calls_min", "Low calls minimum (TC)", "number"],
  ["low_pc_pct_min", "Low PC% minimum (0-100)", "number"],
  ["target_below_pct", "Target below threshold % (0-100)", "number"],
];

export default function ExceptionRulesAdmin() {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setBusy(true);
    api.get("/admin/exception-rules")
      .then((r) => setForm(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setBusy(false));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form) return;
    setSaving(true);
    try {
      const patch = {};
      FIELDS.forEach(([k, _lbl, type]) => {
        let v = form[k];
        if (type === "number" && v !== "" && v != null) v = Number(v);
        patch[k] = v;
      });
      const r = await api.patch("/admin/exception-rules", patch);
      setForm(r.data);
      toast.success("Exception rules saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (busy && !form) return (
    <div className="p-8 text-center text-neutral-400">
      <Loader2 className="h-5 w-5 inline animate-spin" />
    </div>
  );
  if (!form) return null;

  return (
    <div className="p-4 pb-24 space-y-4 max-w-xl">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-neutral-400">
          Admin · Management Exceptions
        </div>
        <h1 className="text-2xl font-extrabold text-neutral-900">
          Exception thresholds
        </h1>
        <div className="text-xs text-neutral-500 mt-1">
          Changes apply to the next `/api/exceptions/detect` run.
        </div>
      </div>

      <Card className="border-neutral-200 rounded-2xl">
        <CardContent className="p-4 space-y-3"
                      data-testid="exc-rules-form">
          {FIELDS.map(([k, lbl, type]) => (
            <div key={k}>
              <label className="block text-[11px] text-neutral-500 mb-1">
                {lbl}
              </label>
              <Input
                type={type}
                value={form[k] ?? ""}
                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                data-testid={`exc-rules-${k.replace(/_/g, "-")}`}
                className="h-9"
              />
            </div>
          ))}
          <Button onClick={save} disabled={saving}
                  data-testid="exc-rules-save"
                  className="tulsi-gradient text-white h-10 rounded-lg mt-2">
            {saving
              ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              : <Save className="h-4 w-4 mr-2" />}
            Save thresholds
          </Button>
          {form._updated_at && (
            <div className="text-[11px] text-neutral-400 pt-2">
              Last updated: {form._updated_at}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
